import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import * as http from "../../utils/http";
import { useCartContext } from "../../customHooks/useCart";
import { loadCustomerFromSession } from "../../customHooks/useCustomer";
import { MainTracker } from "../../PixelTrackers/MainTracker";
import { clientConfig } from "../../config";
import { getWlExperimentTrackingProps, registerWlExperimentSuperProperties } from "../../utils/wlCarePlusExperiment";
import { CarePlusPlanMatrix, CarePlusPlanTier, CarePlusPlanDuration, CarePlusProductFees, CarePlusOneTimeBonus, displayedPlanTiers } from "./types";
import { defaultDurationFor } from "./carePlusTierDefaults";
import { persistCarePlusPlanChoice } from "../CarePlusShared/persistCarePlusPlanChoice";
import { getPlanType } from "../../utils/wlFunnelExperiment";
import { getAssignedChoosePlanOrderVariant } from "../../utils/choosePlanOrderExperiment";
import { toChargeCurrency } from "../../utils/helpers";
import { PLAN_STEP_HISTORY_KEY, rewriteCarePlusHistoryEntry } from "../ChooseTreatmentCarePlus/useChooseTreatmentCarePlus";

// The medication line shown in the cart popup (image, title, dosage label, per-period price).
// `item_price` is the all-in per-product price (official + service + treatment + shipping,
// bundled by reAllocateCartFees). `official_price` is the pure medication cost, used by the
// Care+ shipping card's "next month's payment" (plan) line. It is optional: the free-dosage
// fallback endpoint returns only a single `price` and cannot break the two apart, so it's left
// unset there and consumers fall back to item_price.
export interface CarePlusMedicationLine {
  title: string;
  imageSrc: string;
  quantityStr: string;
  dosage_str?: string;
  quantity_title?: string;
  quantity?: number;
  item_price: number;
  official_price?: number;
  catalog_id?: number;
  product_item_id?: number;
  product_in_stock?: boolean;
  is_pre_orderable?: boolean;
}

// HL-9781 §Phase 3: Choose Plan (Care+) data + selection state.
// Catalog/country-agnostic: everything is driven by the route's mention/country/language.
// Fetches the tier x duration plan matrix (Phase-1 endpoint) once, assigns the A/B variant,
// and persists the chosen (tier, duration) to the cart before continuing to checkout.
//
// Two-component pricing is kept separate end-to-end (HL-9275): plan_fee is the platform fee,
// the medication price lives on the cart's product item. This hook owns only the PLAN side.

// HL-11888: the service fee WL arm stays sticky per browser, across new carts.
const SERVICE_FEE_WL_STORAGE_KEY = "abtest_service_fee_wl_test";

export const getRememberedServiceFeeWlArm = (): string | null => {
  try {
    return localStorage.getItem(SERVICE_FEE_WL_STORAGE_KEY);
  } catch {
    // No storage (private mode): the server rolls for the new cart.
    return null;
  }
};

/** True on the first exposure. Always stores the server's arm, since the cart's arm wins. */
export const rememberServiceFeeWlArm = (arm: string): boolean => {
  try {
    const firstExposure = !localStorage.getItem(SERVICE_FEE_WL_STORAGE_KEY);
    localStorage.setItem(SERVICE_FEE_WL_STORAGE_KEY, arm);
    return firstExposure;
  } catch {
    // No storage (private mode): still count the exposure; a duplicate beats a missing arm.
    return true;
  }
};

const EMPTY_PRODUCT_FEES: CarePlusProductFees = {
  official_price: 0,
  treatment_fee: 0,
  service_fee: 0,
  shipping_fee: 0,
  prescription_only_treatment_fee: 0,
};

const EMPTY_MATRIX: CarePlusPlanMatrix = {
  tiers: [],
  allow_one_time_premium: true,
  show_medication_in_plan_price: false,
  one_time_bonus: null,
  product_fees: EMPTY_PRODUCT_FEES,
};

interface UseChoosePlanCarePlus {
  loading: boolean;
  error: boolean;
  tiers: CarePlusPlanTier[];
  /** HL-11357: false hides the "One Time order" option (product is subscription-only). */
  allowOneTimePremium: boolean;
  /** HL-11489: true marks a tablet product — base-card copy only, not pricing (per catalog). */
  showMedicationInPlanPrice: boolean;
  /** HL-11357: the medication's fees, used to price the non-subscription options. */
  productFees: CarePlusProductFees;
  oneTimeBonus: CarePlusOneTimeBonus | null;
  /** HL-12001: raw section-order A/B/C weights, passed through for the V3 page to bucket on. */
  choosePlanOrderABTestWeights: unknown;
  serviceFeeWlTest: "A" | "B" | null;
  selectedTier: CarePlusPlanTier | null;
  selectedDuration: CarePlusPlanDuration | null;
  selectTier: (tier: string) => void;
  selectDuration: (periodMonths: number) => void;
  continueToCheckout: (opts?: { oneTime?: boolean; prescriptionOnly?: boolean; tier?: string; periodMonths?: number }) => void;
  medication: CarePlusMedicationLine | null;
  submitting: boolean;
  submitError: boolean;
  dismissSubmitError: () => void;
}

// Build the cart-popup medication line from a list of cart products (skip add-ons).
export const lineFromProducts = (products: any[]): CarePlusMedicationLine | null => {
  const p = products.find((x) => !x.is_addon) ?? products[0];
  return p
    ? {
        title: p.title,
        imageSrc: p.imageSrc ?? "",
        quantityStr: p.quantityStr ?? "",
        item_price: p.item_price ?? 0,
        // Fall back to item_price when official_price isn't present (e.g. the free-dosage
        // fallback path, which only carries the display price).
        official_price: p.official_price ?? p.item_price ?? 0,
      }
    : null;
};

export const useChoosePlanCarePlus = (
  initialMedication?: CarePlusMedicationLine | null,
  // HL-12122: the V3 page fires "View Choose Plan Page" itself, once its A/B/C arm is assigned.
  { skipViewTracking = false }: { skipViewTracking?: boolean } = {},
): UseChoosePlanCarePlus => {
  const { country, language, category } = useParams() as { country: string; language: string; category: string };
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { activateSubscriptionInDB, deactivateSubscriptionInDB, getCart, saveCart, cartCurrency } = useCartContext();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [matrix, setMatrix] = useState<CarePlusPlanMatrix>(EMPTY_MATRIX);
  const [selectedTierName, setSelectedTierName] = useState<string | null>(null);
  const [selectedPeriodMonths, setSelectedPeriodMonths] = useState<number | null>(null);
  // Medication line for the cart popup. `undefined` = server reconcile below has not settled
  // yet, so the live in-memory cart is shown meanwhile. The in-page CT → Plan handoff (V3)
  // passes the just-selected medication as `initialMedication`; it is authoritative and skips
  // the reconcile entirely, so we seed with it.
  const [serverMedication, setServerMedication] = useState<CarePlusMedicationLine | null | undefined>(initialMedication ?? undefined);
  // False until the medication lookup below has settled, so the page can hold the loader
  // instead of rendering a card whose medication row pops in a moment later.
  const [medicationResolved, setMedicationResolved] = useState(Boolean(initialMedication));

  const [submitting, setSubmitting] = useState(false);
  // Set when persisting the choice actually fails. The page shows a sticky error rather than
  // carrying the customer to Shipping with a cart that never received the plan.
  const [submitError, setSubmitError] = useState(false);

  // Fire the "View Choose Plan Page" event once (Phase 4).
  const trackedView = useRef(false);
  // Re-entrancy guard. `submitting` gates the CTA, but state does not update synchronously, so
  // a second call in the same tick would fire a duplicate request and a duplicate track event.
  const submittingRef = useRef(false);

  // the in-page CT → Plan handoff has not written the medication to the cart yet, so
  // when the selection is passed in it is named on the request.
  const initialProductItemID = initialMedication?.product_item_id || null;
  const initialCatalogID = initialMedication?.catalog_id || null;

  useEffect(() => {
    const query = new URLSearchParams();
    if (initialProductItemID) query.set("product_item_id", String(initialProductItemID));
    if (initialCatalogID) query.set("catalog_id", String(initialCatalogID));
    // QA override; the server owns the arm because it changes the price.
    const forcedServiceFeeWlVariant = new URLSearchParams(window.location.search).get("service_fee_wl_variant");
    if (forcedServiceFeeWlVariant) query.set("service_fee_wl_variant", forcedServiceFeeWlVariant);
    // A new questionnaire makes a new cart; sending the browser's arm keeps it on its arm.
    const rememberedServiceFeeWlArm = getRememberedServiceFeeWlArm();
    if (rememberedServiceFeeWlArm) query.set("service_fee_wl_arm", rememberedServiceFeeWlArm);
    const queryString = query.toString();

    http
      // CART_ORDER matters: the plan matrix is per-medication (HL-11357). Without the cart
      // headers the server cannot tell which product this is, so it falls back to every plan in
      // the country and prices them with no delivery fee and no bonus.
      .getFromServer(`subscription-plans/${category}/${country}/${language}${queryString ? `?${queryString}` : ""}`, [
        http.MIDDLEWARES.CART_ORDER,
        http.MIDDLEWARES.CUSTOMER_ID,
      ])
      .then((response) => {
        const data: CarePlusPlanMatrix = { ...EMPTY_MATRIX, ...(response?.data ?? {}) };
        setMatrix(data);

        // Default to the first displayed card (Care+ / basic). Never a tier without a card (e.g. the V3
        // monthly tier on a WL2 page) — the V3 page passes its tier explicitly at checkout.
        const autoTier = displayedPlanTiers(data.tiers)[0] ?? null;
        setSelectedTierName(autoTier?.tier ?? null);
        setSelectedPeriodMonths(defaultDurationFor(autoTier)?.period_months ?? null);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [category, country, language]);

  useEffect(() => {
    if (skipViewTracking || loading || error || trackedView.current) return;
    trackedView.current = true;
    // HL-10959: Care+ A/B arm + first-touch entry_point on Choose Plan
    registerWlExperimentSuperProperties();
    MainTracker.track("pv", "View Choose Plan Page", {
      mention: category,
      ...getWlExperimentTrackingProps(),
    });
  }, [skipViewTracking, loading, error, category]);

  // Preferred source: the live in-memory cart (the medication is carried in from Choose
  // Treatment). Recomputed on every render so it appears as soon as the cart is populated,
  // even if that happens a beat after this hook first mounts.
  const inMemoryProducts = getCart()?.products ?? [];
  const inMemoryMedication = useMemo(
    () => (inMemoryProducts.length ? lineFromProducts(inMemoryProducts) : null),
    [inMemoryProducts],
  );

  // Reconcile against the persisted cart on every mount. Choose Treatment writes the selected
  // product directly to the server, so browser back/forward navigation can leave React cart state
  // holding the product from the previous visit. The server result must win once it arrives.
  useEffect(() => {
    // In-page handoff: the medication was just selected and passed in — nothing to reconcile.
    if (initialMedication) return;
    http
      .getFromServer("cart", [http.MIDDLEWARES.CART_ORDER, http.MIDDLEWARES.CUSTOMER_ID])
      .then((response) => {
        const serverCart = response?.data?.cart;
        const products = response?.data?.products;
        if (!serverCart && !Array.isArray(products)) return;
        setServerMedication(null);
        if (products?.length) {
          saveCart({ ...serverCart, products, timekitBooking: serverCart?.timekitBooking || null });
          setServerMedication(lineFromProducts(products));
          return;
        }
        // Fallback: derive the medication from the cart's selected item id.
        const itemId = serverCart?.product_item_id;
        if (!itemId) return;
        // Returned so the `finally` below waits for this second hop too.
        return http
          .getFromServer(`choose-treatment-free-dosage/${category}/${country}/${language}`, [http.MIDDLEWARES.CUSTOMER_ID])
          .then((r2) => {
            for (const prod of r2?.data?.products ?? []) {
              const item = (prod.items ?? []).find((i: any) => i.product_item_id === itemId);
              if (!item) continue;
              setServerMedication({
                title: prod.title,
                imageSrc: prod.image ? `${clientConfig.IMAGES_URL}${prod.image.base_path}/${encodeURIComponent(prod.image.name)}` : "",
                quantityStr: item.custom_quantity_title ?? prod.dosage_str ?? "",
                item_price: Number(item.price ?? 0),
                // No official_price here: the free-dosage endpoint returns only a single
                // `price`, not the official/service breakdown. Consumers fall back to item_price.
              });
              break;
            }
          })
          .catch(() => {});
      })
      .catch(() => {})
      // Settles on failure too: retain a usable in-memory medication when the cart request fails.
      .finally(() => setMedicationResolved(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const medication = serverMedication === undefined ? inMemoryMedication : serverMedication;

  // Nothing to price here → back to Choose Treatment. Reached by refreshing V3's faked
  // /choose_plan URL (the in-page handoff never wrote the medication to the cart) or by any deep
  // link into an empty cart. Waits for the cart lookup to settle, so a V2 cart that does carry a
  // product is never bounced — the previous version keyed off the missing prop and fired on every
  // V2 mount.
  useEffect(() => {
    if (!medicationResolved || medication) return;
    navigate(
      { pathname: `/select_treatment/${country}/${language}/${category}`, search: searchParams.toString() },
      { replace: true },
    );
  }, [medicationResolved, medication, country, language, category, navigate, searchParams]);

  const selectedTier = useMemo(
    () => matrix.tiers.find((tier) => tier.tier === selectedTierName) ?? null,
    [matrix, selectedTierName],
  );

  const selectedDuration = useMemo(() => {
    if (!selectedTier) return null;
    return selectedTier.durations.find((d) => d.period_months === selectedPeriodMonths) ?? defaultDurationFor(selectedTier);
  }, [selectedTier, selectedPeriodMonths]);

  const selectTier = (tierName: string) => {
    const tier = matrix.tiers.find((t) => t.tier === tierName) ?? null;
    setSelectedTierName(tierName);
    const stillHasPeriod = tier?.durations.some((d) => d.period_months === selectedPeriodMonths);
    if (!stillHasPeriod) setSelectedPeriodMonths(defaultDurationFor(tier)?.period_months ?? null);
  };

  const selectDuration = (periodMonths: number) => setSelectedPeriodMonths(periodMonths);

  // Persist the chosen plan to the cart, then continue to checkout (shipping).
  // One Time (§7) is a single purchase with NO subscription: clear any previously-selected
  // plan from the cart (subscription_plan_id = null) so OrderManagerV2 creates a one-off order.
  const continueToCheckout = (opts?: { oneTime?: boolean; prescriptionOnly?: boolean; tier?: string; periodMonths?: number }) => {
    // Choose Plan is guest-accessible (Shipping is not — it sits behind ProtectedCustomerRoute).
    // A guest is NOT asked to log in here: they pick a plan, we persist it on the cart with the
    // endpoints that accept a cart token alone, and registration takes them to Shipping itself.
    const isGuest = !loadCustomerFromSession();

    const oneTime = opts?.oneTime ?? false;
    const prescriptionOnly = opts?.prescriptionOnly ?? false;

    // When a tier/period is passed explicitly (e.g. a per-card CTA that selects and
    // continues in the same click), resolve the duration straight from the matrix instead
    // of selectedTier/selectedDuration — those hooks' state hasn't re-rendered yet.
    const overrideTier = opts?.tier ? matrix.tiers.find((t) => t.tier === opts.tier) ?? null : null;
    const duration = overrideTier
      ? overrideTier.durations.find((d) => d.period_months === opts?.periodMonths) ?? defaultDurationFor(overrideTier)
      : selectedDuration;
    const tier = overrideTier ?? selectedTier;

    if (!oneTime && !duration) return;
    if (submittingRef.current) return;

    if (overrideTier) {
      setSelectedTierName(overrideTier.tier);
      setSelectedPeriodMonths(duration?.period_months ?? null);
    }

    // HL-12122: the plan event.
    const fees = matrix.product_fees ?? EMPTY_PRODUCT_FEES;
    // HL-12122: the choose_plan_order arm on the cart, so server-side events (Paid Order, subscription) get it.
    const choosePlanVariant = getAssignedChoosePlanOrderVariant();
    const cartExperiments: Record<string, string> = choosePlanVariant ? { choose_plan_variant: choosePlanVariant.toLowerCase() } : {};
    // HL-12122: the plan type is the plan sold (tier "monthly" = V3 Monthly plan), not the test cell.
    const planType = getPlanType(oneTime, prescriptionOnly, tier?.tier);
    MainTracker.track("custom", "plan_selected", {
      mention: category,
      plan_type: planType,
      ...(planType === "care_plus" && tier?.tier ? { tier: tier.tier.toLowerCase() } : {}),
      // HL-12264: UK prices in GBP, like the page shows them.
      medication_price: toChargeCurrency(medication?.official_price ?? medication?.item_price, cartCurrency?.rate, country),
      plan_fee: toChargeCurrency(prescriptionOnly
        ? Number(fees.prescription_only_treatment_fee)
        : oneTime
          ? Number(fees.treatment_fee) + Number(fees.service_fee)
          : duration?.plan_fee, cartCurrency?.rate, country),
      shipping_fee: prescriptionOnly ? 0 : toChargeCurrency(Number(fees.shipping_fee), cartCurrency?.rate, country),
    });

    submittingRef.current = true;
    setSubmitting(true);
    setSubmitError(false);

    const proceed = () => {
      // Marks the order as Care+ for ShippingV2 even when there's no plan fee to key off of
      // (one-time / prescription-only), so it always renders the new Care+ checkout layout.
      // A guest goes to registration instead; CheckoutRegister forwards to Shipping on success
      // (and to ID verification / video consultation when those apply), so the destination stays
      // that page's decision rather than something this page has to duplicate.
      const params = new URLSearchParams(searchParams);
      const pathname = isGuest ? `/sign_up/${country}/${language}` : `/shipping/${country}/${language}`;
      // HL-12785: Back from Shipping (or sign-up) and Shipping's header arrow land on Choose Treatment with
      // the cart's medication + dose, instead of reopening the plan step. Continue pushed the plan step its
      // own entry (HL-12777): replace it, so Choose Treatment's entry is the one before. A reopened step
      // (reload, SSO return) only rewrote Choose Treatment's entry: point it back before leaving.
      if (window.history.state?.[PLAN_STEP_HISTORY_KEY]) {
        navigate({ pathname, search: params.toString() }, { replace: true });
        return;
      }
      rewriteCarePlusHistoryEntry(`/select_treatment/${country}/${language}/${category}${window.location.search}`);
      navigate({ pathname, search: params.toString() });
    };

    persistCarePlusPlanChoice(
      { planID: oneTime ? null : duration!.subscription_plan_id, prescriptionOnly, singleWrite: isGuest, experiments: cartExperiments },
      { getCart, saveCart, activateSubscriptionInDB, deactivateSubscriptionInDB },
    )
      .then(() => {
        proceed();
      })
      .catch(() => {
        setSubmitError(true);
      })
      .finally(() => {
        submittingRef.current = false;
        setSubmitting(false);
      });
  };

  return {
    // Hold the loader until the medication is known as well, so the card renders once.
    loading: loading || !medicationResolved,
    error,
    tiers: matrix.tiers,
    allowOneTimePremium: matrix.allow_one_time_premium !== false,
    // Strict: an older or cached response that predates the field leaves the card's standing copy.
    showMedicationInPlanPrice: matrix.show_medication_in_plan_price === true,
    productFees: matrix.product_fees ?? EMPTY_PRODUCT_FEES,
    oneTimeBonus: matrix.one_time_bonus ?? null,
    choosePlanOrderABTestWeights: matrix.choose_plan_order_ab_test_weights ?? null,
    serviceFeeWlTest: matrix.service_fee_wl_test ?? null,
    selectedTier,
    selectedDuration,
    selectTier,
    selectDuration,
    continueToCheckout,
    medication,
    submitting,
    submitError,
    dismissSubmitError: () => setSubmitError(false),
  };
};
